/**
 * Template Key Utilities
 * 
 * Centralized functions for {{key}} extraction, validation, and manipulation
 * Used across ContentTab, QuestionsTab, AnswerContentEditor, PreviewTab
 */

/**
 * Extract all {{key}} placeholders from content
 * @param {string} content - HTML or text content
 * @returns {Array<string>} - Unique sorted key names
 * 
 * Rules:
 * - First extract ALL {{...}} patterns (including invalid ones)
 * - Then validate each key
 * - Must start with letter (A-Z, a-z)
 * - Can contain letters, numbers, underscores
 * - Case-sensitive
 * - Returns unique sorted array
 * 
 * Note: This function extracts ALL {{...}} patterns first, then validation
 * happens in getValidKeys/getInvalidKeys. This allows us to detect invalid
 * keys like {{ccc  A2   asssd }} (with spaces).
 */
export function extractTemplateKeys(content) {
  if (!content || typeof content !== 'string') return [];
  
  // First, extract ALL {{...}} patterns (including invalid ones with spaces, etc.)
  // Regex: /\{\{([^}]+)\}\}/g - matches {{anything}}
  const allPatternsRegex = /\{\{([^}]+)\}\}/g;
  const keys = [];
  let match;
  
  while ((match = allPatternsRegex.exec(content)) !== null) {
    const keyContent = match[1]; // Extract content between {{ }} (keep original, don't trim yet)
    if (keyContent && keyContent.trim()) {
      // Keep original key content (with spaces if any) for validation
      // Validation will check for spaces and reject them
      keys.push(keyContent);
    }
  }
  
  // Return unique sorted keys (by trimmed value for sorting, but keep original for validation)
  return [...new Set(keys)].sort((a, b) => a.trim().localeCompare(b.trim()));
}

/**
 * Validate key name against naming rules
 * @param {string} key - Key name to validate
 * @returns {Object} - { valid: boolean, error: string|null }
 * 
 * Validation Rules:
 * 1. Must start with letter (A-Z, a-z)
 * 2. Only alphanumeric + underscore allowed
 * 3. No Turkish characters (ğ, ı, ö, ü, ş, ç)
 * 4. Max length: 50 characters
 */
export function validateKeyName(key) {
  if (!key || key.trim() === '') {
    return { valid: false, error: 'Key boş olamaz' };
  }
  
  // Rule 0: No spaces (check before trim)
  if (/\s/.test(key)) {
    return { 
      valid: false, 
      error: 'Key boşluk içeremez' 
    };
  }
  
  const trimmedKey = key.trim();
  
  // Rule 1: Must start with letter
  if (!/^[A-Za-z]/.test(trimmedKey)) {
    return { 
      valid: false, 
      error: 'Key harf ile başlamalı (A-Z, a-z)' 
    };
  }
  
  // Rule 2: Only alphanumeric + underscore
  if (!/^[A-Za-z][A-Za-z0-9_]*$/.test(trimmedKey)) {
    return { 
      valid: false, 
      error: 'Key sadece harf, rakam ve alt çizgi (_) içerebilir' 
    };
  }
  
  // Rule 3: No Turkish characters
  const turkishChars = /[ğĞıİöÖüÜşŞçÇ]/;
  if (turkishChars.test(trimmedKey)) {
    return { 
      valid: false, 
      error: 'Key Türkçe karakter içeremez (ğ→g, ı→i, ö→o, ü→u, ş→s, ç→c)' 
    };
  }
  
  // Rule 4: Max length (UX constraint)
  if (trimmedKey.length > 50) {
    return { 
      valid: false, 
      error: 'Key en fazla 50 karakter olabilir' 
    };
  }
  
  // All rules passed
  return { valid: true, error: null };
}

/**
 * Get valid keys (extracted and validated)
 * @param {string} content - Template content HTML
 * @returns {Array<string>} - Valid key names only (trimmed)
 * 
 * Usage: QuestionsTab, AnswerContentEditor (show in table)
 */
export function getValidKeys(content) {
  const allKeys = extractTemplateKeys(content);
  return allKeys
    .filter(key => validateKeyName(key).valid)
    .map(key => key.trim()); // Trim valid keys for use in question definitions
}

/**
 * Get invalid keys (extracted but validation failed)
 * @param {string} content - Template content HTML
 * @returns {Array<string>} - Invalid key names for warning
 * 
 * Usage: ContentTab (show warning alert, no action)
 */
export function getInvalidKeys(content) {
  const allKeys = extractTemplateKeys(content);
  return allKeys.filter(key => !validateKeyName(key).valid);
}

/**
 * Cleanup orphan questions (questions for keys deleted from content)
 * @param {Array<Object>} questions - Current question array
 * @param {Array<string>} validKeys - Valid keys from content
 * @returns {Array<Object>} - Filtered questions (orphans removed)
 * 
 * Usage: TemplateEditorPage (useEffect on content change)
 * 
 * Logic:
 * - Remove questions where q.key is not in validKeys
 * - Keep questions without keys (defensive, shouldn't happen)
 */
export function cleanupOrphanQuestions(questions = [], validKeys = []) {
  return questions.filter(q => {
    // Keep questions without keys (shouldn't happen, but defensive)
    if (!q.key || q.key.trim() === '') return false;
    
    // Keep only if key exists in validKeys
    return validKeys.includes(q.key);
  });
}

/**
 * Recursive cleanup of orphan nested questions in answer branches
 * @param {Array<Object>} answers - Answer branches array
 * @returns {Array<Object>} - Cleaned answers with orphan nested questions removed
 * 
 * Usage: AnswerContentEditor (recursive cleanup for nested questions in answer branches)
 * 
 * Logic:
 * - For each answer branch, get valid keys from paragraph
 * - Cleanup nested questions (first level)
 * - Recursively cleanup nested questions in nested answer branches
 */
export function cleanupOrphanNestedQuestionsRecursive(answers = []) {
  return answers.map(answer => {
    if (!answer.content) return answer;
    
    const { paragraph, nestedQuestions = [] } = answer.content;
    
    // Get valid keys from paragraph
    const validKeys = getValidKeys(paragraph || '');
    
    // Cleanup first level nested questions
    const cleanedNestedQuestions = cleanupOrphanQuestions(nestedQuestions, validKeys);
    
    // Recursively cleanup nested questions in nested answer branches
    const cleanedNestedQuestionsWithRecursive = cleanedNestedQuestions.map(nestedQuestion => {
      if (!nestedQuestion.answers || nestedQuestion.answers.length === 0) {
        return nestedQuestion;
      }
      
      // Recursively cleanup nested answer branches
      const cleanedAnswers = cleanupOrphanNestedQuestionsRecursive(nestedQuestion.answers);
      
      return {
        ...nestedQuestion,
        answers: cleanedAnswers
      };
    });
    
    return {
      ...answer,
      content: {
        ...answer.content,
        nestedQuestions: cleanedNestedQuestionsWithRecursive
      }
    };
  });
}

/**
 * Check if template is static (no placeholders)
 * @param {string} content - Template content HTML
 * @returns {boolean} - True if no {{key}} found
 * 
 * Usage: PreviewTab, QuestionsTab (show info message)
 */
export function isStaticTemplate(content) {
  return extractTemplateKeys(content).length === 0;
}

/**
 * Generate display text from key name
 * "Kiralayan_Ad" → "Kiralayan Ad"
 * "TC_No" → "TC No"
 * @param {string} key - Key name
 * @returns {string} - Human-readable display text
 * 
 * Usage: QuestionFormModal (auto-generate displayText when key is pre-filled)
 */
export function generateDisplayTextFromKey(key) {
  if (!key) return '';
  
  // Replace underscores with spaces
  let displayText = key.replace(/_/g, ' ');
  
  // Capitalize first letter of each word
  displayText = displayText
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
  
  return displayText;
}

/**
 * Şablonda gerçekten kullanılan TÜM key'leri döndürür:
 * - Ana contentHtml içindeki {{key}}'ler
 * - Tüm cevap paragraflarındaki {{key}}'ler (nested olanlar dahil)
 */
export function getAllUsedKeysInTemplate(contentHtml = '', questions = []) {
  const usedKeys = new Set(getValidKeys(contentHtml || ''));

  const traverseAnswers = (answers = []) => {
    if (!Array.isArray(answers)) return;

    answers.forEach((answer) => {
      if (!answer || !answer.content) return;

      const paragraph = answer.content.paragraph || '';
      // Paragraftaki key'leri ekle
      getValidKeys(paragraph).forEach((k) => usedKeys.add(k));

      const nestedQuestions = answer.content.nestedQuestions || [];
      // Nested soruların kendi answer'larını da dolaş (recursive)
      nestedQuestions.forEach((nestedQ) => {
        if (Array.isArray(nestedQ.answers) && nestedQ.answers.length > 0) {
          traverseAnswers(nestedQ.answers);
        }
      });
    });
  };

  // Tüm soruların cevaplarını gez
  questions.forEach((q) => {
    if (Array.isArray(q.answers) && q.answers.length > 0) {
      traverseAnswers(q.answers);
    }
  });

  return Array.from(usedKeys);
}
